import { useEffect, useState } from "react";

const mimeTypeMap: { [key: string]: string } = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        "docx",
};

export const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith("image/")) return "🖼️";
    if (fileType === "text/plain") return "📄";
    if (fileType.includes("excel") || fileType.includes("spreadsheet"))
        return "📊";
    if (fileType.includes("pdf")) return "📑";
    if (
        fileType.includes("word") ||
        fileType.includes("docx") ||
        fileType.includes("doc")
    )
        return "📝";
    if (fileType.includes("exe")) return "⚙️";
    return "📁";
};

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const toDateTimeStr = (
    timestamp: string | number | null | undefined
): string => {
    if (!timestamp) return "N/A";

    try {
        const timestampNum =
            typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
        const dateObj = new Date(timestampNum);

        const day = dateObj.getDate().toString().padStart(2, "0");
        const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const year = dateObj.getFullYear();
        const hours = dateObj.getHours().toString().padStart(2, "0");
        const minutes = dateObj.getMinutes().toString().padStart(2, "0");

        return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
        console.error("Error formatting timestamp:", error);
        return "Invalid date";
    }
};

export const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

function getMimeTypeExtension(type: string): string {
    return mimeTypeMap[type] || "";
}

export const validateFile = (file: File) => {
    const fileExtension = getMimeTypeExtension(file.type);
    const extensionFromName = file.name.split(".").pop()?.toLowerCase();
    const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = ["pdf", "doc", "docx"];
    const maxSize = 100 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
        return {
            valid: false,
            error: `Invalid file type: ${file.type}`,
        };
    }

    if (!fileExtension || fileExtension !== extensionFromName) {
        return {
            valid: false,
            error: `File extension doesn't match the file type: ${file.name}`,
        };
    }

    if (!allowedExtensions.includes(fileExtension)) {
        return {
            valid: false,
            error: `File type not allowed. Allowed types: ${allowedExtensions.join(
                ", "
            )}`,
        };
    }

    if (file.size > maxSize) {
        return {
            valid: false,
            error: `File too large. Maximum size is ${
                maxSize / (1024 * 1024)
            }MB`,
        };
    }

    return { valid: true, extension: fileExtension, error: "" };
};
